import type { HTMLProps, ReactNode } from 'react';

import { Typography } from '@remarkable/ark-web';
import { useForm } from 'react-hook-form';

import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import type { FeedbackTargetValues } from 'src/api/endpoints/storeApi.types';
import {
  useAddFeedbackComment,
  useFeedbackComment,
  useFeedbackValue,
} from 'src/api/queries/feedback';
import { cn } from 'src/utils/classNamesHelper';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';

import { Trans } from '@lingui/react/macro';
import { ThumbsDown, ThumbsUp } from '@remarkable/ark-icons';
import { Button } from './Button';
import { Form } from './Form';
import { Link } from './Link';
import { NotificationBox } from './NotificationBox';

interface FeedbackProps {
  feedbackTargetId: FeedbackTargetValues;
  title: ReactNode;
  commentOnly?: boolean;
  commentContext?: Record<string, string>;
}

export function Feedback({
  title,
  feedbackTargetId,
  commentContext,
  commentOnly = false,
  ...props
}: HTMLProps<HTMLDivElement> & FeedbackProps) {
  const feedbackValue = useFeedbackValue(feedbackTargetId);
  const addFeedbackComment = useAddFeedbackComment(feedbackTargetId);
  const feedbackComment = useFeedbackComment(feedbackTargetId);
  const form = useForm<{
    comment: string;
  }>();

  if (commentOnly) {
    return (
      <div
        {...props}
        className={cn(
          'rounded-sm flex flex-col items-center justify-center gap-24 px-16 py-32',
          props.className
        )}
      >
        {!feedbackComment.isSuccess && (
          <>
            <Typography variant="interface-md" fontWeight="medium" leadingNone>
              {title}
            </Typography>

            <Form
              {...form}
              className="flex w-full animate-fade-in-up flex-col items-center gap-24"
              onSubmit={(values) => {
                const comment = commentContext
                  ? `${Object.entries(commentContext)
                      .map(([key, value]) => `Context:\n${key}: ${value}`)
                      .join('\n')}\n\nComment:\n${values.comment}`
                  : values.comment;

                tracker.trackEvent(
                  new ButtonClicked({
                    component_location: feedbackTargetId,
                    action: 'feedback comment',
                    text: comment,
                  })
                );

                feedbackComment.mutate({ comment });
              }}
            >
              <div className="flex w-full flex-col items-center gap-8">
                <textarea
                  {...form.register('comment', { required: true })}
                  className="rounded-sm min-h-[100px] w-full max-w-[560px] border border-neutral-light-8 bg-neutral-light-1 p-12"
                  placeholder="Enter your answer..."
                />

                <Typography
                  variant="interface-xs"
                  className="text-center leading-125"
                >
                  <Trans>
                    If you need help or have questions,{' '}
                    <Link
                      inline
                      to={SUPPORT_URL.CONTACT_SUPPORT}
                      trackingLocation={`feedback ${feedbackTargetId}`}
                    >
                      contact support
                    </Link>
                    .<br />
                    Please don&apos;t include personal information.
                  </Trans>
                </Typography>
              </div>

              <Button
                type="submit"
                size="medium"
                variant="secondary"
                loading={feedbackComment.isPending}
                data-cy="feedback-submit-button"
              >
                <Trans>Send feedback</Trans>
              </Button>
            </Form>

            <NotificationBox error={feedbackComment.error} />
          </>
        )}

        {feedbackComment.isSuccess && (
          <Typography variant="interface-md" fontWeight="medium" leadingNone>
            <Trans>Thank you for your feedback!</Trans>
          </Typography>
        )}
      </div>
    );
  }

  return (
    <div
      {...props}
      className={cn(
        'rounded-sm flex flex-col items-center justify-center gap-24 px-16 py-32',
        props.className
      )}
    >
      {!feedbackValue.isSuccess && (
        <>
          <Typography
            variant="interface-md"
            fontWeight="medium"
            leadingNone
            className="text-center"
          >
            {title}
          </Typography>

          <div className="flex gap-16">
            <Button
              variant="tertiary"
              aria-label="Thumbs up feedback"
              size="medium"
              data-cy="feedback-thumbs-up-button"
              onClick={() => {
                tracker.trackEvent(
                  new ButtonClicked({
                    component_location: feedbackTargetId,
                    action: 'feedback thumbs up',
                    text: '[thumbs up icon]',
                  })
                );
                feedbackValue.mutate({ feedbackValue: true });
              }}
              loading={
                feedbackValue.isPending &&
                feedbackValue.variables?.feedbackValue === true
              }
            >
              <ThumbsUp />
            </Button>

            <Button
              variant="tertiary"
              aria-label="Thumbs down feedback"
              size="medium"
              data-cy="feedback-thumbs-down-button"
              onClick={() => {
                tracker.trackEvent(
                  new ButtonClicked({
                    component_location: feedbackTargetId,
                    action: 'feedback thumbs down',
                    text: '[thumbs down icon]',
                  })
                );
                feedbackValue.mutate({ feedbackValue: false });
              }}
              loading={
                feedbackValue.isPending &&
                feedbackValue.variables?.feedbackValue === false
              }
            >
              <ThumbsDown />
            </Button>
          </div>

          {feedbackValue.isError && (
            <NotificationBox error={feedbackValue.error}>
              <Trans>
                We couldn&apos;t submit your feedback. Please try again later or
                contact support if the problem persists.
              </Trans>
            </NotificationBox>
          )}
        </>
      )}

      {feedbackValue.isSuccess && !addFeedbackComment.isSuccess && (
        <>
          <Typography variant="interface-md" fontWeight="medium" leadingNone>
            <Trans>Thanks, is there anything you&apos;d like to add?</Trans>
          </Typography>

          <Form
            {...form}
            className="flex w-full animate-fade-in-up flex-col items-center gap-24"
            onSubmit={(values) => {
              const comment = commentContext
                ? `${Object.entries(commentContext)
                    .map(([key, value]) => `Context:\n${key}: ${value}`)
                    .join('\n')}\n\nComment:\n${values.comment}`
                : values.comment;

              tracker.trackEvent(
                new ButtonClicked({
                  component_location: feedbackTargetId,
                  action: 'feedback add comment',
                  text: comment,
                })
              );
              if (!feedbackValue.data?.id) {
                return;
              }

              addFeedbackComment.mutate({
                comment: comment,
                instanceId: feedbackValue.data.id,
              });
            }}
          >
            <div className="flex w-full flex-col items-center gap-8">
              <textarea
                {...form.register('comment', { required: true })}
                className="rounded-sm min-h-[100px] w-full max-w-[560px] border border-neutral-light-8 bg-neutral-light-1 p-12"
                placeholder="Enter your answer..."
              />

              <Typography
                variant="interface-xs"
                className="text-center leading-125"
              >
                <Trans>
                  If you need help or have questions,{' '}
                  <Link
                    inline
                    to={SUPPORT_URL.CONTACT_SUPPORT}
                    trackingLocation={`feedback ${feedbackTargetId}`}
                  >
                    contact support
                  </Link>
                  .<br />
                  Please don&apos;t include personal information.
                </Trans>
              </Typography>
            </div>

            <Button
              type="submit"
              size="medium"
              variant="secondary"
              loading={addFeedbackComment.isPending}
              data-cy="feedback-submit-button"
            >
              <Trans>Send feedback</Trans>
            </Button>
          </Form>

          {addFeedbackComment.isError && (
            <NotificationBox error={addFeedbackComment.error}>
              <Trans>
                We couldn&apos;t submit your comment. Please try again later or
                contact support if the problem persists.
              </Trans>
            </NotificationBox>
          )}
        </>
      )}

      {addFeedbackComment.isSuccess && (
        <Typography variant="interface-md" fontWeight="medium" leadingNone>
          <Trans>Thank you for your feedback!</Trans>
        </Typography>
      )}
    </div>
  );
}
